import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

export interface StreamServeProjekt {
  externalId: string;
  projektName: string;
  projektVersion: number;
}

export interface StreamServeProjektInput {
  projektName: string;
  projektVersion: number;
}

const BASE_PATH = '/konfigurationen/streamserveprojekte';

export const useStreamServeProjekte = () => {
  const { axiosInstance } = useApiClient();

  return useQuery<StreamServeProjekt[]>({
    queryKey: queryKeys.konfiguration.streamServeProjekte(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: StreamServeProjekt[] }>(
        BASE_PATH,
      );
      return response.data.data;
    },
  });
};

export const useStreamServeProjektByExternalId = (externalId: string) => {
  const { axiosInstance } = useApiClient();

  return useQuery<StreamServeProjekt>({
    queryKey:
      queryKeys.konfiguration.streamServeProjektByExternalId(externalId),
    queryFn: async () => {
      const response = await axiosInstance.get<StreamServeProjekt>(
        `${BASE_PATH}/${externalId}`,
      );
      return response.data;
    },
    enabled: !!externalId,
  });
};

export const useCreateStreamServeProjekt = () => {
  const { axiosInstance } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: StreamServeProjektInput) => {
      const response = await axiosInstance.post(BASE_PATH, params);
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success(
        `StreamServe-Projekt ${variables.projektName} wurde angelegt.`,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.streamServeProjekte(),
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Anlegen des StreamServe-Projekts.');
      // eslint-disable-next-line no-console
      console.error('Failed to create streamServeProjekt:', error);
    },
  });
};

export const useUpdateStreamServeProjekt = () => {
  const { axiosInstance } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: StreamServeProjektInput & { externalId: string },
    ) => {
      const { externalId, ...body } = params;
      const response = await axiosInstance.put(
        `${BASE_PATH}/${externalId}`,
        body,
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success(
        `StreamServe-Projekt ${variables.projektName} wurde aktualisiert.`,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.streamServeProjekte(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.streamServeProjektByExternalId(
          variables.externalId,
        ),
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Aktualisieren des StreamServe-Projekts.');
      // eslint-disable-next-line no-console
      console.error('Failed to update streamServeProjekt:', error);
    },
  });
};

export const useDeleteStreamServeProjekt = () => {
  const { axiosInstance } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (externalId: string) => {
      const response = await axiosInstance.delete(`${BASE_PATH}/${externalId}`);
      return response.data;
    },
    onSuccess: (_, externalId) => {
      toast.success(`StreamServe-Projekt ${externalId} wurde gelöscht.`);
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.streamServeProjekte(),
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Löschen des StreamServe-Projekts.');
      // eslint-disable-next-line no-console
      console.error('Failed to delete streamServeProjekt:', error);
    },
  });
};
